/**
 * 展示预约列表 Composable
 * 提供 activeAppointments、displayedAppointments 计算属性
 */

export default function useDisplayedAppointments() {
  return {
    computed: {
      activeAppointments() {
        const filteredAppointments = this.appointmentData.filter((item) => {
          if (!item || !item.startTime || !item.endTime) {
            return false;
          }
  
          const now = new Date();
          const startTime = this.parseTimeString(item.startTime);
          const endTime = this.parseTimeString(item.endTime);
  
          if (!startTime || !endTime) {
            return false;
          }
  
          // 显示所有预约（包括未开始、治疗中、已结束）
          return true;
        });
  
        // 按照新的展示逻辑排序：优先展示治疗中患者，然后展示治疗结束患者
        let sortedAppointments = filteredAppointments.sort((a, b) => {
          const statusA = this.getTreatmentStatus(a);
          const statusB = this.getTreatmentStatus(b);
          
          // 定义状态优先级：治疗中 > 治疗未开始 > 已结束
          const getPriority = (status) => {
            if (status === '治疗中') return 1;
            if (status === '治疗未开始') return 2;
            if (status === '已结束') return 3;
            return 4;
          };
          
          const priorityA = getPriority(statusA);
          const priorityB = getPriority(statusB);
          
          // 如果优先级不同，按优先级排序
          if (priorityA !== priorityB) {
            return priorityA - priorityB;
          }
          
          // 如果优先级相同，进行二级排序
          if (statusA === '治疗中' && statusB === '治疗中') {
            // 虚拟数据固定排在真实数据之前，便于占满第一页；同组内按剩余时间排序
            const va = !!(a.isMock || a.dataType === 1 || !a.roomId);
            const vb = !!(b.isMock || b.dataType === 1 || !b.roomId);
            if (va !== vb) return va ? -1 : 1;
            const remainingTimeA = this.getRemainingTimeInMinutes(a);
            const remainingTimeB = this.getRemainingTimeInMinutes(b);
            return remainingTimeA - remainingTimeB;
          } else if (statusA === '已结束' && statusB === '已结束') {
            // 都是已结束，按结束日期时间距离当前最近的顺序（近的排前面）
            const now = new Date();
            const getEndDateTime = (appt) => {
              let appointmentDate = appt.appointmentDate ? new Date(appt.appointmentDate) : new Date(appt.startTime);
              if (isNaN(appointmentDate.getTime())) {
                appointmentDate = new Date();
              }
              const appointmentDateOnly = new Date(appointmentDate);
              appointmentDateOnly.setHours(0, 0, 0, 0);
              const withDate = this.parseTimeStringWithDate(appt.endTime, appointmentDateOnly);
              const fallback = this.parseTimeString(appt.endTime);
              return withDate || fallback || new Date(0);
            };
            const endA = getEndDateTime(a);
            const endB = getEndDateTime(b);
            const diffA = Math.abs(now.getTime() - endA.getTime());
            const diffB = Math.abs(now.getTime() - endB.getTime());
            return diffA - diffB;
          } else if (statusA === '治疗未开始' && statusB === '治疗未开始') {
            // 都是未开始，按开始时间排序（即将开始的排前面）
            const startTimeA = this.parseTimeString(a.startTime);
            const startTimeB = this.parseTimeString(b.startTime);
            return startTimeA - startTimeB;
          }
          
          return 0;
        });
  
        const MAX_VIRTUAL_APPOINTMENTS = 4;
        const virtualAppointments = sortedAppointments.filter(item => 
          item && (item.isMock || item.dataType === 1 || !item.roomId)
        );
        
        if (virtualAppointments.length > MAX_VIRTUAL_APPOINTMENTS) {
          const byPriority = [...virtualAppointments].sort((a, b) => {
            const pa = this.getTreatmentStatus(a) === '治疗中' ? 0 : 1;
            const pb = this.getTreatmentStatus(b) === '治疗中' ? 0 : 1;
            if (pa !== pb) return pa - pb;
            return 0;
          });
          const keepSet = new Set(byPriority.slice(0, MAX_VIRTUAL_APPOINTMENTS));
          logger.debug(`[虚拟数据条数控制] activeAppointments 中检测到 ${virtualAppointments.length} 个虚拟数据，保留 ${MAX_VIRTUAL_APPOINTMENTS} 条`, {
            totalVirtual: virtualAppointments.length,
            kept: [...keepSet].map(i => i.patientName)
          });
          sortedAppointments = sortedAppointments.filter(item => {
            if (!item) return false;
            const isVirtual = item.isMock || item.dataType === 1 || !item.roomId;
            if (isVirtual) {
              return keepSet.has(item);
            }
            return true;
          });
        }
        
        // 实现患者数量补充逻辑
        const ongoingPatients = sortedAppointments.filter(item => this.getTreatmentStatus(item) === '治疗中');
        const completedPatients = sortedAppointments.filter(item => this.getTreatmentStatus(item) === '已结束');
        const upcomingPatients = sortedAppointments.filter(item => this.getTreatmentStatus(item) === '治疗未开始');
        
        let finalAppointments = [];
        
        // 1. 优先展示当前正在治疗的患者
        finalAppointments = [...ongoingPatients];
        
        const pageSize = this.welcomeMessage ? 2 : 4;
        const ongoingCount = finalAppointments.length;
        if (this.welcomeMessage) {
          const targetCount = Math.max(pageSize, Math.ceil(ongoingCount / pageSize) * pageSize);
          let needToAdd = Math.max(0, targetCount - finalAppointments.length);
          if (needToAdd > 0 && completedPatients.length > 0) {
            const patientsToAdd = completedPatients.slice(0, needToAdd);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          let stillNeed = targetCount - finalAppointments.length;
          if (stillNeed > 0 && upcomingPatients.length > 0) {
            const patientsToAdd = upcomingPatients.slice(0, stillNeed);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          if (ongoingPatients.length === 0 && completedPatients.length > 0) {
            finalAppointments = completedPatients.slice(0, pageSize);
          }
          if (ongoingPatients.length === 0 && completedPatients.length === 0 && upcomingPatients.length > 0) {
            const targetCount = Math.ceil(upcomingPatients.length / pageSize) * pageSize;
            finalAppointments = upcomingPatients.slice(0, targetCount);
          }
        } else {
          const currentCount = finalAppointments.length;
          const nextMultiple = Math.max(pageSize, Math.ceil(currentCount / pageSize) * pageSize);
          let needToAdd = nextMultiple - currentCount;
          if (needToAdd > 0 && completedPatients.length > 0) {
            const patientsToAdd = completedPatients.slice(0, needToAdd);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          let stillNeed = nextMultiple - finalAppointments.length;
          if (stillNeed > 0 && upcomingPatients.length > 0) {
            const patientsToAdd = upcomingPatients.slice(0, stillNeed);
            finalAppointments = [...finalAppointments, ...patientsToAdd];
          }
          if (ongoingPatients.length === 0 && completedPatients.length > 0) {
            finalAppointments = completedPatients.slice(0, pageSize);
          }
          if (ongoingPatients.length === 0 && completedPatients.length === 0 && upcomingPatients.length > 0) {
            const targetCount = Math.ceil(upcomingPatients.length / pageSize) * pageSize;
            finalAppointments = upcomingPatients.slice(0, targetCount);
          }
        }
        
        return finalAppointments;
      },
  
      displayedAppointments() {
        // 如果当前页是视频页或全屏欢迎语页，返回空数组
        if (this.isVideoPage || this.isFullscreenWelcomePage) {
          return [];
        }
        
        // 1. 确定每页需要的数量
        // 半屏欢迎语：welcomeMessage 存在且 isFullscreen !== 1
        const isHalfScreenWelcome = this.welcomeMessage && 
          !(this.welcomeConfig && (this.welcomeConfig.isFullscreen === 1 || this.welcomeConfig.isFullscreen === '1'));
        const pageSize = isHalfScreenWelcome ? 2 : 4;
        
        // 2. 直接从 appointmentData 筛选，而不是从 activeAppointments
        const allAppointments = this.appointmentData.filter((item) => {
          if (!item || !item.startTime || !item.endTime) {
            return false;
          }
          return true;
        });
        
        // 3. 分离治疗中患者和已结束患者
        const ongoingPatients = allAppointments.filter(item => 
          this.getTreatmentStatus(item) === '治疗中'
        );
        
        // 对治疗中排序：虚拟数据在前（占满第一页），其余按剩余时间
        ongoingPatients.sort((a, b) => {
          const va = !!(a.isMock || a.dataType === 1 || !a.roomId);
          const vb = !!(b.isMock || b.dataType === 1 || !b.roomId);
          if (va !== vb) return va ? -1 : 1;
          const remainingTimeA = this.getRemainingTimeInMinutes(a);
          const remainingTimeB = this.getRemainingTimeInMinutes(b);
          return remainingTimeA - remainingTimeB;
        });
        
        const completedPatients = allAppointments.filter(item => 
          this.getTreatmentStatus(item) === '已结束'
        );
        
        // 4. 对已结束患者按结束时间排序（最近的优先）
        completedPatients.sort((a, b) => {
          const now = new Date();
          const getEndDateTime = (appt) => {
            let appointmentDate = appt.appointmentDate ? new Date(appt.appointmentDate) : new Date(appt.startTime);
            if (isNaN(appointmentDate.getTime())) {
              appointmentDate = new Date();
            }
            const appointmentDateOnly = new Date(appointmentDate);
            appointmentDateOnly.setHours(0, 0, 0, 0);
            const withDate = this.parseTimeStringWithDate(appt.endTime, appointmentDateOnly);
            const fallback = this.parseTimeString(appt.endTime);
            return withDate || fallback || new Date(0);
          };
          const endA = getEndDateTime(a);
          const endB = getEndDateTime(b);
          const diffA = Math.abs(now.getTime() - endA.getTime());
          const diffB = Math.abs(now.getTime() - endB.getTime());
          return diffA - diffB;
        });
        
        // 如果没有数据，返回空位
        if (ongoingPatients.length === 0 && completedPatients.length === 0) {
          const emptyResult = [];
          for (let i = 0; i < pageSize; i++) {
            emptyResult.push(null);
          }
          return emptyResult;
        }
        
        // 5. 计算当前页的治疗中患者
        const startIndex = this.currentPage * pageSize;
        const endIndex = startIndex + pageSize;
        const currentPageOngoing = ongoingPatients.slice(startIndex, endIndex);
        
        // 6. 计算需要补位的数量
        const needToFill = pageSize - currentPageOngoing.length;
        
        // 7. 从已结束患者中补位（排除已经在其他页显示的患者）
        // 计算前面页已经使用的已结束患者数量
        let usedCompletedCount = 0;
        for (let page = 0; page < this.currentPage; page++) {
          const pageStart = page * pageSize;
          const pageOngoing = ongoingPatients.slice(pageStart, pageStart + pageSize);
          const pageOngoingCount = pageOngoing.length;
          const pageNeedFill = Math.max(0, pageSize - pageOngoingCount);
          usedCompletedCount += pageNeedFill;
        }
        
        // 获取当前页可用的已结束患者（从已使用的数量之后开始）
        const availableCompleted = completedPatients.slice(usedCompletedCount);
        
        // 8. 补位
        const fillPatients = availableCompleted.slice(0, needToFill);
        const result = [...currentPageOngoing, ...fillPatients];
        
        // 9. 如果还不够，用 null 补位
        while (result.length < pageSize) {
          result.push(null);
        }
        
        return result;
      },
  
      // 轮播相关计算属性已提取到 usePageRotation composable
      // totalPages, isVideoPage, isFullscreenWelcomePage, needsRotation, currentPageDisplay 现在从 composable 中获取
          
    }
  };
}
